import {
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton,
  Button, Text, Box, Divider, Link, VStack,
} from '@chakra-ui/react';

export default function TermsDialog({ isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="20px" maxH="82vh" mx={{ base: 3, md: "auto" }}>

        <ModalHeader pb={3} pt={6} px={7} borderBottom="1px solid #F2F2F2">
          <Text fontSize="20px" fontWeight="800" color="#1A1A1A" letterSpacing="-0.3px">
            Terms & Conditions
          </Text>
          <Text fontSize="12px" color="#9E9E9E" fontWeight="normal" mt="4px">
            Effective Date: April 3, 2026 · Walnut TMA LLC · Dubai, UAE
          </Text>
        </ModalHeader>
        <ModalCloseButton top={5} right={5} />

        <ModalBody py={6} px={7}>
          <VStack align="stretch" spacing={5}>

            <InfoBanner>
              Alpherra is an AI-powered engineering feasibility and supplier pricing platform.
              By using the Platform, you agree to these Terms.{" "}
              <Link href="mailto:contact@alpherra.com" color="#CF008A">contact@alpherra.com</Link>
              {" · "}
              <Link href="https://www.alpherra.com" color="#CF008A" isExternal>www.alpherra.com</Link>
            </InfoBanner>

            <Clause number="1" title="About Alpherra">
              Alpherra is developed by Walnut TMA, a fabrication and public art company based in Dubai, UAE, designed to assist engineering and fabrication professionals in evaluating project feasibility and obtaining indicative supplier pricing.
            </Clause>

            <Clause number="2" title="Eligibility">
              <BulletList items={[
                'You are at least 18 years of age.',
                'You are acting on behalf of a legitimate business entity or as a qualified professional.',
                'You have the authority to bind your organisation to these Terms.',
                'Your use complies with all applicable laws and regulations.',
              ]} />
            </Clause>

            <Clause number="3" title="Account Registration & Security">
              You are responsible for providing accurate information, maintaining credential confidentiality,
              and all activity under your account. Notify us immediately at{" "}
              <Link href="mailto:contact@alpherra.com" color="#CF008A">contact@alpherra.com</Link>{" "}
              if you suspect unauthorised access.
            </Clause>

            <Clause number="4" title="Subscription Plans & Payment">
              <BulletList items={[
                'Teams Plan — AED/USD monthly rate as published on the Platform at time of subscription.',
                'Enterprise Plan — Custom pricing agreed upon by separate written agreement.',
              ]} />
              <Text fontSize="13px" color="#4F4F4F" lineHeight="1.7" mt={2}>
                All fees are non-refundable except where required by law. Pricing may change with 30 days' written notice.
              </Text>
            </Clause>

            <Clause number="5" title="Acceptable Use">
              <Text fontSize="13px" color="#4F4F4F" lineHeight="1.7" mb={2}>You must not:</Text>
              <BulletList items={[
                'Use the Platform for fraudulent, misleading, or illegal purposes.',
                'Reverse-engineer, decompile, or extract source code or AI models.',
                'Scrape or systematically download data from the Platform.',
                'Share credentials with unauthorised parties.',
                'Introduce malicious code or interfere with Platform infrastructure.',
                'Use the Platform to develop a competing product or service.',
              ]} />
            </Clause>

            <Clause number="6" title="Intellectual Property">
              All IP rights in the Platform are owned by or licensed to Walnut TMA / Alpherra. You are granted a limited,
              non-exclusive, non-transferable licence for internal business use during your active subscription.
              You retain ownership of data you submit ("User Data").
            </Clause>

            <Clause number="7" title="AI Outputs — Disclaimer">
              <WarningBox>
                Alpherra's AI outputs are indicative and for informational purposes only. They do not constitute
                professional engineering advice, certified structural assessments, or binding commercial quotations.
              </WarningBox>
              <BulletList items={[
                'AI outputs may contain errors, omissions, or inaccuracies.',
                'You are solely responsible for verifying any output before acting on it.',
                'Consult qualified engineers or suppliers before committing to any project.',
              ]} />
            </Clause>

            <Clause number="8" title="Confidentiality">
              Both parties agree to keep confidential any non-public information disclosed in connection with the Platform,
              except information that is publicly available, independently developed, or required by law.
            </Clause>

            <Clause number="9" title="Limitation of Liability">
              <BulletList items={[
                "Alpherra's total liability shall not exceed fees paid in the three (3) months preceding the claim.",
                'Alpherra is not liable for indirect, incidental, consequential, or exemplary damages.',
                'These limitations apply regardless of the legal theory under which the claim is brought.',
              ]} />
            </Clause>

            <Clause number="10" title="Indemnification">
              You agree to indemnify and hold harmless Alpherra, Walnut TMA, and their officers from any claims,
              damages, and expenses arising from your use of the Platform, violation of these Terms,
              or infringement of third-party rights.
            </Clause>

            <Clause number="11" title="Termination">
              Either party may terminate at any time. Access ceases at the end of the billing period.
              Alpherra may suspend accounts that violate these Terms immediately and without notice.
            </Clause>

            <Clause number="12" title="Modifications to Terms">
              Material changes will be communicated at least 14 days before taking effect via email or
              in-platform notification. Continued use constitutes acceptance.
            </Clause>

            <Clause number="13" title="Governing Law & Dispute Resolution">
              These Terms are governed by the laws of the UAE. Disputes are subject to the exclusive jurisdiction
              of the courts of Dubai, UAE. Parties shall first attempt good-faith negotiation.
            </Clause>

            <Clause number="14" title="Severability & Entire Agreement">
              If any provision is unenforceable, it shall be modified minimally. These Terms and the Privacy Policy
              constitute the entire agreement between you and Alpherra.
            </Clause>

            <Divider />
            <Text fontSize="12px" color="#ABABAB" textAlign="center">
              Last updated April 3, 2026 · © 2026 Walnut TMA LLC. All rights reserved.
            </Text>

          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid #F2F2F2" px={7} pb={5}>
          <Button bg="#CF008A" color="white" borderRadius="10px" _hover={{ bg: '#D933A1' }} onClick={onClose} w="100%">
            I understand
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Helpers ──────────────────────────────────────────────────
function Clause({ number, title, children }) {
  return (
    <Box borderBottom="1px solid #F5F5F5" pb={4}>
      <Text fontSize="13px" fontWeight="700" color="#1A1A1A" mb={2}>
        {number}. {title}
      </Text>
      <Box>
        {typeof children === 'string' ? (
          <Text fontSize="13px" color="#6D6D6D" lineHeight="1.8">{children}</Text>
        ) : children}
      </Box>
    </Box>
  );
}

function BulletList({ items }) {
  return (
    <VStack align="stretch" spacing="5px">
      {items.map((item, i) => (
        <Text key={i} fontSize="13px" color="#6D6D6D" lineHeight="1.8" pl={3}>
          — {item}
        </Text>
      ))}
    </VStack>
  );
}

function InfoBanner({ children }) {
  return (
    <Box borderLeft="3px solid #CF008A" pl={4} py={1}>
      <Text fontSize="13px" color="#6D6D6D" lineHeight="1.8">{children}</Text>
    </Box>
  );
}

function WarningBox({ children }) {
  return (
    <Box bg="#FAFAFA" borderRadius="10px" px={4} py={3} mb={3}>
      <Text fontSize="13px" color="#4F4F4F" fontWeight="500" lineHeight="1.8">{children}</Text>
    </Box>
  );
}

function SectionLabel({ children, ...props }) {
  return (
    <Text fontSize="12px" fontWeight="600" color="#9E9E9E" textTransform="uppercase"
      letterSpacing="0.6px" mb={2} {...props}>
      {children}
    </Text>
  );
}